import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Modal,
  ModalBody,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../../../Components/Common/HelpArticleLink";
import { HELP_ARTICLE_UNITS } from "../../../../helpers/helpCenter";
import { entityFormKey } from "../../../../helpers/entityEditForm";
import {
  isQuillHtmlEmpty,
  normalizeQuillHtml,
} from "../../../../Components/Common/QuillFormField";
import { richTextPreviewPlain } from "../../../../helpers/richTextSanitize";
import UnitScheduleModal from "./UnitScheduleModal";
import UnitForm, {
  DEFAULT_COUNTRY,
  DEFAULT_COUNTRY_CODE,
  isBrazilianLocale,
  UnitFormValues,
} from "./UnitForm";
import {
  createUnit,
  CreateUnitPayload,
  deleteUnit,
  getUnit,
  getUnits,
  Unit,
  updateUnit,
} from "../../../../services/unitService";
import { PaginationMeta } from "../../../../types/api";

const PER_PAGE = 15;
const SEARCH_DEBOUNCE_MS = 400;
const SCHEDULE_DEEP_LINK_PULSE_MS = 2000;

const formatPhoneDisplay = (phoneNumber: string): string => {
  const digits = phoneNumber.replace(/\D/g, "");
  if (digits.length <= 2) {
    return digits.length ? `(${digits}` : "";
  }
  if (digits.length <= 7) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
};

const formatPhoneLabel = (
  countryCode: string,
  phoneNumber: string,
  fallback: string
): string => {
  const display = formatPhoneDisplay(phoneNumber);
  if (!display) {
    return fallback;
  }
  return countryCode === "55" ? display : `+${countryCode} ${display}`;
};

const formatPostalDisplay = (postalCode?: string | null): string => {
  const digits = (postalCode || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 5) {
    return digits;
  }
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const emptyFormValues = (): UnitFormValues => ({
  name: "",
  phone_country_code: DEFAULT_COUNTRY_CODE,
  country: DEFAULT_COUNTRY,
  phone_display: "",
  postal_code_display: "",
  street: "",
  street_number: "",
  neighborhood: "",
  city: "",
  state: "",
  knowledge_base: "",
});

const mapUnitToFormValues = (unit: Unit): UnitFormValues => ({
  name: unit.name,
  phone_country_code: unit.country_code || DEFAULT_COUNTRY_CODE,
  country: DEFAULT_COUNTRY,
  phone_display: formatPhoneDisplay(unit.phone_number),
  postal_code_display: formatPostalDisplay(unit.postal_code),
  street: unit.street ?? "",
  street_number: unit.street_number ?? "",
  neighborhood: unit.neighborhood ?? "",
  city: unit.city ?? "",
  state: unit.state ?? "",
  knowledge_base: unit.knowledge_base ?? "",
});

const stripHtmlPreview = (html: string): string => richTextPreviewPlain(html);

const buildUnitPayload = (values: UnitFormValues, locale: string): CreateUnitPayload => ({
  name: values.name.trim(),
  country_code: isBrazilianLocale(locale)
    ? DEFAULT_COUNTRY_CODE
    : values.phone_country_code.replace(/\D/g, "") || DEFAULT_COUNTRY_CODE,
  phone_number: values.phone_display.replace(/\D/g, ""),
  street: values.street.trim() || null,
  street_number: values.street_number.trim() || null,
  neighborhood: values.neighborhood.trim() || null,
  city: values.city.trim() || null,
  state: values.state.trim().toUpperCase() || null,
  postal_code: values.postal_code_display.replace(/\D/g, "") || null,
  knowledge_base: normalizeQuillHtml(values.knowledge_base),
});

const UnitsList = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const scheduleDeepLinkHandledRef = useRef(false);

  document.title = t("units.pageTitle");

  const [units, setUnits] = useState<Unit[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Unit | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [scheduleUnit, setScheduleUnit] = useState<Unit | null>(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [pulseScheduleUnitId, setPulseScheduleUnitId] = useState<number | null>(null);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().trim().required(t("units.validation.nameRequired")),
        phone_country_code: isBrazilianLocale(i18n.language)
          ? Yup.string()
          : Yup.string()
              .required(t("units.validation.countryCodeRequired"))
              .matches(/^\d{1,4}$/, t("units.validation.countryCodeInvalid")),
        phone_display: Yup.string()
          .required(t("units.validation.phoneRequired"))
          .test("phone-digits", t("units.validation.phoneInvalid"), (value) => {
            const digits = (value || "").replace(/\D/g, "");
            return digits.length >= 8 && digits.length <= 15;
          }),
        knowledge_base: Yup.string(),
      }),
    [t, i18n.language]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const fetchUnits = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getUnits({
        page,
        per_page: PER_PAGE,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      setUnits(response.data);
      setMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("units.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, t]);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  useEffect(() => {
    if (pulseScheduleUnitId === null) {
      return;
    }

    const timer = window.setTimeout(() => {
      setPulseScheduleUnitId(null);
    }, SCHEDULE_DEEP_LINK_PULSE_MS);

    return () => window.clearTimeout(timer);
  }, [pulseScheduleUnitId]);

  const triggerScheduleButtonPulse = useCallback((unitId: number) => {
    setPulseScheduleUnitId(unitId);
  }, []);

  useEffect(() => {
    const scheduleUnitId = searchParams.get("schedule");

    if (!scheduleUnitId || scheduleDeepLinkHandledRef.current || loading) {
      return;
    }

    const unitId = Number(scheduleUnitId);

    if (!Number.isFinite(unitId) || unitId <= 0) {
      setSearchParams({}, { replace: true });
      return;
    }

    scheduleDeepLinkHandledRef.current = true;

    const openScheduleModal = async () => {
      const listedUnit = units.find((unit) => unit.id === unitId);

      if (listedUnit) {
        setScheduleUnit(listedUnit);
        setScheduleModalOpen(true);
        triggerScheduleButtonPulse(unitId);
        setSearchParams({}, { replace: true });
        return;
      }

      try {
        const response = await getUnit(unitId);
        setScheduleUnit(response.data);
        setScheduleModalOpen(true);
        triggerScheduleButtonPulse(unitId);
      } catch (error: any) {
        toast.error(error?.data || t("units.toast.loadError"));
      } finally {
        setSearchParams({}, { replace: true });
      }
    };

    void openScheduleModal();
  }, [loading, searchParams, setSearchParams, t, triggerScheduleButtonPulse, units]);

  const formInitialValues = useMemo(
    () => (editingUnit ? mapUnitToFormValues(editingUnit) : emptyFormValues()),
    [editingUnit]
  );

  const formik = useFormik<UnitFormValues>({
    enableReinitialize: true,
    initialValues: formInitialValues,
    validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);
      const payload = buildUnitPayload(values, i18n.language);

      try {
        if (editingUnit) {
          const response = await updateUnit(editingUnit.id, payload);
          toast.success(response.message);
        } else {
          const response = await createUnit(payload);
          toast.success(response.message);
        }
        closeOffcanvas();
        if (!editingUnit && page !== 1) {
          setPage(1);
        } else {
          await fetchUnits();
        }
      } catch (error: any) {
        toast.error(error?.data || t("units.toast.saveError"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const closeOffcanvas = () => {
    setOffcanvasOpen(false);
    setEditingUnit(null);
    setFormLoading(false);
  };

  const openCreate = () => {
    setEditingUnit(null);
    setFormLoading(false);
    setOffcanvasOpen(true);
  };

  const openEdit = async (unit: Unit) => {
    setEditingUnit(null);
    setFormLoading(true);
    setOffcanvasOpen(true);

    try {
      const response = await getUnit(unit.id);
      setEditingUnit(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("units.toast.loadError"));
      closeOffcanvas();
    } finally {
      setFormLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      const response = await deleteUnit(deleteTarget.id);
      toast.success(response.message);
      setDeleteModal(false);
      setDeleteTarget(null);

      const isLastItemOnPage = units.length === 1 && page > 1;
      if (isLastItemOnPage) {
        setPage((current) => current - 1);
      } else {
        await fetchUnits();
      }
    } catch (error: any) {
      toast.error(error?.data || t("units.toast.deleteError"));
    }
  };

  const totalPages = meta?.last_page ?? 1;
  const hasActiveSearch = debouncedSearch.length > 0;
  const notAvailable = t("units.common.notAvailable");

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("units.breadcrumb.title")}
            pageTitle={t("units.breadcrumb.pageTitle")}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center flex-wrap gap-2">
                  <h4 className="card-title mb-0 flex-grow-1">{t("units.card.title")}</h4>
                  <HelpArticleLink articlePath={HELP_ARTICLE_UNITS} className="align-self-center" />
                  <Button color="primary" onClick={openCreate}>
                    <i className="ri-add-line align-bottom me-1" />
                    {t("units.actions.new")}
                  </Button>
                </CardHeader>

                <CardBody>
                  <Row className="mb-3">
                    <Col md={5} lg={4}>
                      <div className="search-box">
                        <Input
                          type="text"
                          className="form-control"
                          placeholder={t("units.search.placeholder")}
                          value={searchTerm}
                          onChange={(event) => setSearchTerm(event.target.value)}
                        />
                        <i className="ri-search-line search-icon" />
                      </div>
                    </Col>
                  </Row>

                  {loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : units.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <p className="mb-3">
                        {hasActiveSearch
                          ? t("units.empty.noResults", { term: debouncedSearch })
                          : t("units.empty.noData")}
                      </p>
                      {!hasActiveSearch && (
                        <Button color="soft-primary" onClick={openCreate}>
                          {t("units.actions.createFirst")}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table hover className="align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{t("units.table.name")}</th>
                              <th>{t("units.table.phone")}</th>
                              <th>{t("units.form.knowledge_base_label")}</th>
                              <th className="text-end" style={{ width: 120 }}>
                                {t("units.table.actions")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {units.map((unit) => (
                              <tr key={unit.id}>
                                <td className="fw-medium">{unit.name}</td>
                                <td>
                                  {formatPhoneLabel(
                                    unit.country_code,
                                    unit.phone_number,
                                    notAvailable
                                  )}
                                </td>
                                <td className="text-muted">
                                  {unit.knowledge_base &&
                                  !isQuillHtmlEmpty(unit.knowledge_base)
                                    ? (() => {
                                        const preview = stripHtmlPreview(unit.knowledge_base);
                                        return preview.slice(0, 60) + (preview.length > 60 ? "…" : "");
                                      })()
                                    : notAvailable}
                                </td>
                                <td className="text-end">
                                  <Button
                                    color="soft-info"
                                    size="sm"
                                    className={`me-1${
                                      pulseScheduleUnitId === unit.id
                                        ? " units-schedule-btn-pulse"
                                        : ""
                                    }`}
                                    title={t("studio.units.schedules.actions.open")}
                                    onClick={() => {
                                      setScheduleUnit(unit);
                                      setScheduleModalOpen(true);
                                    }}
                                  >
                                    <i className="ri-time-line" />
                                  </Button>
                                  <Button
                                    color="soft-primary"
                                    size="sm"
                                    className="me-1"
                                    title={t("units.actions.edit")}
                                    onClick={() => openEdit(unit)}
                                  >
                                    <i className="ri-pencil-line" />
                                  </Button>
                                  <Button
                                    color="soft-danger"
                                    size="sm"
                                    title={t("units.actions.delete")}
                                    onClick={() => {
                                      setDeleteTarget(unit);
                                      setDeleteModal(true);
                                    }}
                                  >
                                    <i className="ri-delete-bin-line" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      {meta && totalPages > 1 && (
                        <Row className="align-items-center mt-3 g-3">
                          <Col sm={6}>
                            <p className="text-muted mb-0">
                              {t("units.pagination.showing", {
                                from: meta.from ?? 0,
                                to: meta.to ?? 0,
                                total: meta.total,
                              })}
                            </p>
                          </Col>
                          <Col sm={6}>
                            <Pagination className="pagination-separated justify-content-sm-end mb-0">
                              <PaginationItem disabled={page <= 1}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page > 1) {
                                      setPage(page - 1);
                                    }
                                  }}
                                >
                                  {t("units.pagination.previous")}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem active>
                                <PaginationLink href="#">
                                  {page} / {totalPages}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem disabled={page >= totalPages}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page < totalPages) {
                                      setPage(page + 1);
                                    }
                                  }}
                                >
                                  {t("units.pagination.next")}
                                </PaginationLink>
                              </PaginationItem>
                            </Pagination>
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Offcanvas
        isOpen={offcanvasOpen}
        toggle={closeOffcanvas}
        direction="end"
        className="border-0"
      >
        <OffcanvasHeader toggle={closeOffcanvas} className="bg-light">
          {editingUnit ? t("units.form.editTitle") : t("units.form.createTitle")}
        </OffcanvasHeader>
        <OffcanvasBody>
          {formLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary">{t("clients.loading")}</Spinner>
            </div>
          ) : (
          <Form
            key={entityFormKey(editingUnit?.id)}
            onSubmit={(event) => {
              event.preventDefault();
              formik.handleSubmit();
            }}
          >
            <UnitForm formik={formik} editingUnitId={editingUnit?.id ?? null} />

            <div className="d-flex gap-2 mt-4">
              <Button color="light" type="button" onClick={closeOffcanvas}>
                {t("units.actions.cancel")}
              </Button>
              <Button color="primary" type="submit" disabled={submitting}>
                {submitting ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <i className="ri-save-line align-bottom me-1" />
                )}
                {t("units.actions.save")}
              </Button>
            </div>
          </Form>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-delete-bin-line display-5 text-danger" />
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t("units.delete.title")}</h4>
              <p className="text-muted mx-4 mb-0">
                {deleteTarget
                  ? t("units.delete.description", { name: deleteTarget.name })
                  : t("units.delete.fallback")}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <Button color="light" onClick={() => setDeleteModal(false)}>
              {t("units.actions.cancel")}
            </Button>
            <Button color="danger" onClick={confirmDelete}>
              {t("units.actions.confirmDelete")}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <UnitScheduleModal
        unit={scheduleUnit}
        isOpen={scheduleModalOpen}
        onClose={() => {
          setScheduleModalOpen(false);
          setScheduleUnit(null);
        }}
      />
    </React.Fragment>
  );
};

export default UnitsList;
